// @ts-strict-ignore
import React from 'react'
import classnames from 'classnames'
import { CollectionItem, ScreenSection } from 'mytours-read-common'
import AccessModal from 'mobile_web/modules/common/components/access-modal'

import { getDirMode, isRTL, format } from '../../platform'
import { defaultMapStyle } from '../functions'

import { Button, GridRow, GridCol } from '@mytours/components'
import SegmentButtons from '../../common/components/SegmentButtons'
import Modal from '../../common/components/modal'
import { ScrollContent } from 'mobile_web/modules/common/components/content'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'

import MapboxMap from './MapboxMap'
import { MapContainer, RouteProfile } from '../types'

import strings from '../../../strings'

import '../styles.less'
import '../styles/inAppDirections.less'
import { DEFAULT_APP_INFO, loadAppMetaDataFromDisk } from 'mobile_web/modules/settings/functions'
import { GetAppShowResponse } from 'mytours-read-api-typescript'

interface Props extends MapContainer {
	showModal: boolean
	activeItem: CollectionItem | ScreenSection
	toggleModal: (show: boolean) => void
	profileType: RouteProfile
}

interface State {
	modalDidPresent: boolean
	routeProfile: RouteProfile
	isInstructionsOpened: boolean
	legsData: any // TODO: add type
	showDirectionsModal: boolean
	distanceUnit: GetAppShowResponse.DistanceUnitEnum
}

const DEFAULT_ROUTE_PROFILE = 'walking'
const ROUTE_PROFILES = ['walking', 'cycling', 'driving']

const INITIAL_STATE: State = {
	modalDidPresent: false,
	routeProfile: DEFAULT_ROUTE_PROFILE,
	isInstructionsOpened: false,
	legsData: null,
	showDirectionsModal: false,
	distanceUnit: null,
}

export default class InAppDirections extends React.Component<Props, State> {
	state = INITIAL_STATE

	constructor(props: Props) {
		super(props)

		this.state.routeProfile =
			props.profileType && ROUTE_PROFILES.includes(props.profileType) ? props.profileType : DEFAULT_ROUTE_PROFILE
	}

	componentDidMount(): void {
		this.setDistanceUnit()
	}

	componentDidUpdate = (prevProps: Props) => {
		if (!prevProps.showModal && this.props.showModal) {
			this.setState({
				legsData: null,
				isInstructionsOpened: false,
				routeProfile: ROUTE_PROFILES.includes(this.props.profileType) ? this.props.profileType : DEFAULT_ROUTE_PROFILE,
			})
		}
	}

	private setDistanceUnit = async (): Promise<void> => {
		const appData = await loadAppMetaDataFromDisk(DEFAULT_APP_INFO.projectId)

		this.setState({
			distanceUnit: appData?.distance_unit || null,
		})
	}

	toggleModalDidPresent = (status: boolean) => {
		this.setState({ modalDidPresent: status })
		if (!status) {
			this.props.toggleModal(false)
		}
	}

	toggleBottomSheet = () => {
		this.setState(prev => ({
			isInstructionsOpened: !prev.isInstructionsOpened,
		}))
	}

	setLegsData = (legsData: any) => {
		this.setState({ legsData })
	}

	switchRouteProfile = (routeProfile: RouteProfile) => {
		this.setState({ routeProfile })
	}

	onAccessCheckDone = () => {
		this.setState({ showDirectionsModal: true })
	}

	renderModal = (): JSX.Element => {
		const { showModal, activeItem, containerType, containerId } = this.props
		const { modalDidPresent, routeProfile, showDirectionsModal } = this.state

		const endCoordinates =
			activeItem && activeItem.lat && activeItem.lng
				? {
						lat: Number(activeItem.lat),
						lng: Number(activeItem.lng),
					}
				: undefined

		return (
			<>
				{showModal && <AccessModal types={['location']} onCheckDone={this.onAccessCheckDone} />}
				<Modal
					isOpen={showDirectionsModal && showModal}
					cssClass="map-modal"
					onDidPresent={() => this.toggleModalDidPresent(true)}
					onDidDismiss={() => this.toggleModalDidPresent(false)}
				>
					<ScrollContent>
						{this.renderMapControls()}
						{modalDidPresent ? (
							<GridRow className="map-row">
								<MapboxMap
									items={[]}
									currentItemIndex={-1}
									mapStyle={`mapbox://styles/${defaultMapStyle}`}
									showDirections={true}
									routeProfile={routeProfile}
									defaultZoom={15}
									endCoordinates={endCoordinates}
									setLegsData={this.setLegsData}
									onCloseMap={() => this.props.toggleModal(false)}
									containerType={containerType}
									containerId={containerId}
								/>
							</GridRow>
						) : (
							<ImagePlaceholder style={{ width: '100%', height: '100%' }} />
						)}
						{this.renderBottomSheet()}
					</ScrollContent>
				</Modal>
			</>
		)
	}

	renderMapControls = (): JSX.Element => (
		<>
			<div className={classnames('map-controls-close-button', getDirMode())}>
				<Button variant="clear" className="close-map-button" onClick={() => this.props.toggleModal(false)}>
					<i className="fal fa-xmark" />
				</Button>
			</div>
		</>
	)

	renderSwitchButton = () => (
		<GridCol className="switch-button" size="auto">
			<Button variant="clear">
				<i className={`fal fa-${this.state.isInstructionsOpened ? 'chevron-donw' : 'chevron-up'}`} />
			</Button>
		</GridCol>
	)

	renderDirectionSteps = () => {
		const { legsData, distanceUnit } = this.state

		return (
			<GridRow className="direction-steps">
				{legsData &&
					Array.isArray(legsData.steps) &&
					legsData.steps.map((step, index: number) => {
						const distance = step.distance ? format.distance(step.distance, distanceUnit) : false
						let icon = step.maneuver.modifier
							? step.maneuver.modifier.replace(/\s+/g, '-').toLowerCase()
							: step.maneuver.type.replace(/\s+/g, '-').toLowerCase()

						if (step.maneuver.type === 'arrive' || step.maneuver.type === 'depart') {
							icon = step.maneuver.type
						}

						if (step.maneuver.type === 'roundabout' || step.maneuver.type === 'rotary') {
							icon = 'roundabout'
						}

						const lng = step.maneuver.location[0]
						const lat = step.maneuver.location[1]

						return (
							<div key={index} data-lat={lng} data-lng={lat} className={classnames('directions-step', getDirMode())}>
								<span className={`directions-icon directions-icon-${icon}`} />
								<div className="directions-step-maneuver">{step.maneuver.instruction}</div>
								{distance && <div className="directions-step-distance">{distance}</div>}
							</div>
						)
					})}
			</GridRow>
		)
	}

	renderInfoRow = () => {
		const { legsData, distanceUnit } = this.state

		return (
			<GridRow className={classnames('instructions-container', getDirMode())} onClick={this.toggleBottomSheet}>
				{isRTL() && this.renderSwitchButton()}
				<GridCol className="destination-properties">
					{legsData && (
						<>
							<span className="timing">{format.duration(legsData.duration / 60)}</span>
							<span className="distance">{format.distance(legsData.distance, distanceUnit)}</span>
						</>
					)}
				</GridCol>
				{!isRTL() && this.renderSwitchButton()}
			</GridRow>
		)
	}

	renderBottomSheet = () => {
		const { isInstructionsOpened, routeProfile } = this.state

		return (
			<div className={classnames('direction-instructions', isInstructionsOpened && 'open')}>
				{this.renderDirectionSteps()}
				<SegmentButtons
					selectedValue={routeProfile}
					onChange={value => this.switchRouteProfile(value as RouteProfile)}
					buttons={[
						{
							value: 'walking',
							label: strings.route_walking,
						},
						{
							value: 'cycling',
							label: strings.route_cycling,
						},
						{
							value: 'driving',
							label: strings.route_driving,
						},
					]}
				/>
				{this.renderInfoRow()}
			</div>
		)
	}

	render = () => this.renderModal()
}
